import {
  Box,
  ChevronLeftIcon,
  ChevronRightIcon,
  FilterMenuGroup,
  FilterMenuOption,
  IconButton,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  PaperclipIcon,
  PlusIcon,
  PromptInputMenuButton,
  Row,
  Text,
  Tooltip,
} from "@hightouch/htui";
import {
  type ComponentType,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import type { AttachmentSource } from "./attachment-source";
import { useToolbarLayout } from "./composer-toolbar";

/** A composer toolbar action described as data, so the toolbar decides how to render it. */
export type ComposerLeftAction = {
  key: string;
  /** Brief action text: the row label in the menu, the tooltip/aria-label inline. */
  label: string;
  icon: ComponentType;
  /** The corresponding inline control stays visible in a wide toolbar. */
  compactOnly?: boolean;
  /** A selected mode renders an inline pill when space permits. */
  hasInlinePill?: boolean;
  isDisabled?: boolean;
  /** Says why a disabled row is unavailable. */
  disabledTooltip?: string;
  isLoading?: boolean;
  /**
   * Render as a toggle reflecting `isChecked`: it flips in place and keeps the
   * menu open (dismiss with outside-click or Escape). Without it the action is
   * treated as a picker and dismisses the menu after `onClick`.
   */
  toggle?: boolean;
  isChecked?: boolean;
  /** Count shown beside the menu row (e.g. number of attached components). */
  badge?: number;
} & (
  | { onClick: () => void; submenu?: undefined; menu?: undefined }
  | {
      onClick?: undefined;
      submenu?: undefined;
      /** Choices shown inside this menu instead of opening another popover. */
      menu: ReactNode;
      menuTitle?: string;
    }
  | {
      onClick?: undefined;
      /** Rows the menu shows in place of its own when this action is picked. */
      submenu: ComposerSubmenu;
      menu?: undefined;
    }
);

export type ComposerSubmenu = {
  /** Checkable rows that flip in place and keep the menu open. */
  items: ComposerSubmenuItem[];
  /** A last row below a divider, such as a link to manage the items. */
  footer?: { label: string; icon: ComponentType; onClick: () => void };
};

export type ComposerSubmenuItem = {
  key: string;
  label: string;
  isChecked: boolean;
  onClick: () => void;
};

type ComposerLeftActionsProps = {
  actions: ComposerLeftAction[];
  /**
   * The host composer is disabled, so every action goes with it — whatever the
   * caller passed. Lives here rather than in each host because the "+" trigger's
   * own state is derived from the actions below it.
   */
  isDisabled?: boolean;
};

/**
 * Stable empty list, so a composer that takes no attachment sources doesn't
 * hand the toolbar a fresh array on every render.
 */
export const EMPTY_ATTACHMENT_SOURCES: AttachmentSource[] = [];

/**
 * The built-in "attach a file" action. Every composer offers it, and it sorts
 * ahead of the attachment sources so the plain upload path stays in a
 * predictable spot as surfaces add or drop sources.
 */
export const buildAddFilesAction = ({
  onClick,
  isDisabled,
  isLoading,
}: Pick<ComposerLeftAction, "isDisabled" | "isLoading"> & {
  onClick: () => void;
}): ComposerLeftAction => ({
  key: "add-files",
  label: "Add files",
  icon: PaperclipIcon,
  onClick,
  isDisabled,
  isLoading,
});

/**
 * Always a "+". Mount anything an action opens outside this component — the
 * menu is lazy and unmounts its items on close.
 */
export const ComposerLeftActions = ({
  actions: allActions,
  isDisabled = false,
}: ComposerLeftActionsProps) => {
  const isCompact = useToolbarLayout() === "menu";
  const actions = allActions.filter(
    (action) => !action.compactOnly || isCompact,
  );
  const [submenuKey, setSubmenuKey] = useState<string | null>(null);
  // The row that opened the submenu, focused again on Back.
  const [returnFocusKey, setReturnFocusKey] = useState<string | null>(null);
  const focusRowRef = useRef<HTMLButtonElement>(null);
  // Swapping rows unmounts the focused one, so focus moves to the Back row or the opener.
  // A frame later: a new row has no menu index on mount, and focusing it then refocuses the list.
  useEffect(() => {
    if (submenuKey === null && returnFocusKey === null) return;
    const frame = requestAnimationFrame(() => focusRowRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [submenuKey, returnFocusKey]);

  if (actions.length === 0) {
    return null;
  }

  // A lone picker skips the menu; a lone toggle or submenu still needs the
  // menu, to show its on/off state or its rows.
  const [only] = actions;
  if (
    actions.length === 1 &&
    only &&
    !only.toggle &&
    !only.submenu &&
    !only.menu
  ) {
    return (
      <Tooltip message={only.label} placement="bottom">
        <IconButton
          aria-label={only.label}
          icon={PlusIcon}
          type="button"
          variant="tertiary"
          shape="circle"
          isDisabled={isDisabled || only.isDisabled}
          isLoading={only.isLoading}
          onClick={only.onClick}
        />
      </Tooltip>
    );
  }

  // The single-action path disables/loads its button per-action; the menu
  // trigger has no per-action state to reflect. Only disable it when every
  // action is unavailable (disabled or busy) — otherwise it would open a menu
  // of dead items. Don't set isLoading on the trigger: a spinner there is
  // non-interactive, so one busy action would lock the whole menu and block the
  // still-available ones. Each item shows its own busy state once the menu opens.
  const hasActiveTools =
    isCompact && actions.some((action) => action.isChecked);
  const allUnavailable =
    isDisabled ||
    actions.every((action) => action.isDisabled || action.isLoading);
  const submenuAction = actions.find(
    (action) => action.key === submenuKey && (action.submenu || action.menu),
  );
  const openSubmenu = (key: string) => {
    setReturnFocusKey(null);
    setSubmenuKey(key);
  };
  const closeSubmenu = () => {
    setReturnFocusKey(submenuKey);
    setSubmenuKey(null);
  };
  const onRowKeyDown = (event: KeyboardEvent) => {
    if (submenuAction) {
      if (event.key === "ArrowLeft") closeSubmenu();
      return;
    }
    const opener =
      event.target instanceof Element
        ? event.target.closest<HTMLElement>("[data-submenu-key]")
        : null;
    const key = opener?.dataset.submenuKey;
    if (event.key === "ArrowRight" && key) openSubmenu(key);
  };

  // Pickers first, then toggles, as two groups.
  const pickers = actions.filter((action) => !action.toggle);
  const toggles = actions.filter((action) => action.toggle);
  const renderRow = (action: ComposerLeftAction) => (
    <MenuItem
      key={action.key}
      ref={action.key === returnFocusKey ? focusRowRef : undefined}
      icon={action.icon}
      isDisabled={isDisabled || action.isDisabled || action.isLoading}
      onClick={() =>
        action.submenu || action.menu
          ? openSubmenu(action.key)
          : action.onClick?.()
      }
      // Toggles flip in place, so keep the menu open for another flip;
      // pickers open a modal and let the menu dismiss itself. A submenu
      // swaps the rows in place. (Chakra owns the item's role, so state
      // is conveyed via the label.)
      {...(action.toggle
        ? {
            closeOnSelect: false,
            "aria-label": `${action.label} ${action.isChecked ? "on" : "off"}`,
          }
        : {})}
      {...(action.submenu || action.menu
        ? {
            closeOnSelect: false,
            "aria-haspopup": "menu",
            "data-submenu-key": action.key,
          }
        : {})}
    >
      <Row align="center" justify="space-between" gap={4} flex={1}>
        {action.label}
        {action.submenu || action.menu ? (
          <Box as={ChevronRightIcon} boxSize={4} color="text.secondary" />
        ) : action.toggle ? (
          <ToggleSwitchVisual isChecked={action.isChecked} />
        ) : (
          action.badge != null &&
          action.badge > 0 && (
            <Text size="sm" color="text.secondary">
              {action.badge}
            </Text>
          )
        )}
      </Row>
    </MenuItem>
  );
  const renderItem = (action: ComposerLeftAction) => (
    <Tooltip
      key={action.key}
      message={action.disabledTooltip}
      // A loading row is disabled for now, so it does not say why it is empty.
      isDisabled={
        !action.isDisabled ||
        action.isLoading ||
        action.disabledTooltip === undefined
      }
      placement="right"
    >
      {renderRow(action)}
    </Tooltip>
  );

  return (
    // Chakra drops a menu item's own onKeyDown, so arrow keys are read as they
    // bubble out of the portaled list. display: contents keeps the rows the
    // menu's direct children and leaves the toolbar layout alone.
    <Box display="contents" onKeyDown={onRowKeyDown}>
      {/* Positioning on open, not mount: a parked off-screen chat would otherwise
          leave the hidden menu past the window edge, scrolling the page sideways. */}
      <Menu
        computePositionOnMount={false}
        onClose={() => {
          setSubmenuKey(null);
          setReturnFocusKey(null);
        }}
      >
        {/* Family trigger so its hover matches the pills beside it; an inactive
            compact pill never reveals its label, so it stays an icon-only disc. */}
        <Box
          display="inline-flex"
          flexShrink={0}
          position="relative"
          _hover={
            hasActiveTools && !allUnavailable
              ? { "& button": { bg: "primary.background" } }
              : undefined
          }
          // The compact + keeps its fixed icon width; active pill labels would expand it.
          sx={
            hasActiveTools && !allUnavailable
              ? {
                  "& button": {
                    bg: "primary.background",
                    color: "primary.base",
                  },
                  "& button[aria-expanded=true]": {
                    bg: "primary.background",
                  },
                }
              : undefined
          }
        >
          <PromptInputMenuButton
            variant="compact"
            icon={PlusIcon}
            label="More actions"
            tooltip="More actions"
            isDisabled={allUnavailable}
          />
          {hasActiveTools && (
            <Box
              as="span"
              role="img"
              aria-label="Active selections in More actions"
              position="absolute"
              top={0.5}
              right={0.5}
              boxSize={1.5}
              borderRadius="full"
              bg={allUnavailable ? "icon.disabled" : "primary.base"}
              pointerEvents="none"
            />
          )}
        </Box>
        <MenuList>
          {submenuAction?.submenu ? (
            <SubmenuRows
              label={submenuAction.label}
              submenu={submenuAction.submenu}
              isDisabled={isDisabled}
              backRowRef={focusRowRef}
              onBack={closeSubmenu}
            />
          ) : submenuAction?.menu ? (
            <>
              <MenuItem
                ref={focusRowRef}
                icon={ChevronLeftIcon}
                {...{
                  closeOnSelect: false,
                  "aria-label": `Back from ${submenuAction.menuTitle ?? submenuAction.label}`,
                }}
                onClick={closeSubmenu}
              >
                {submenuAction.menuTitle ?? submenuAction.label}
              </MenuItem>
              <MenuDivider />
              {submenuAction.menu}
            </>
          ) : (
            <>
              {pickers.map(renderItem)}
              {pickers.length > 0 && toggles.length > 0 && <MenuDivider />}
              {toggles.map(renderItem)}
            </>
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

/** A submenu's rows, shown in the "+" menu in place of its own. ArrowLeft or Back returns. */
const SubmenuRows = ({
  label,
  submenu,
  isDisabled,
  backRowRef,
  onBack,
}: {
  label: string;
  submenu: ComposerSubmenu;
  isDisabled: boolean;
  backRowRef: RefObject<HTMLButtonElement>;
  onBack: () => void;
}) => {
  return (
    <>
      <MenuItem
        ref={backRowRef}
        icon={ChevronLeftIcon}
        onClick={onBack}
        {...{
          closeOnSelect: false,
          "aria-label": `Back from ${label}`,
        }}
      >
        {label}
      </MenuItem>
      {/* MenuList takes no height, so the rows scroll in their own box. */}
      <Box
        maxHeight="xs"
        overflowY="auto"
        // FilterMenuOption does not expose check placement; order its internal icon after the label.
        sx={{ ".chakra-menu__icon-wrapper": { order: 1 } }}
      >
        <FilterMenuGroup
          type="checkbox"
          value={submenu.items
            .filter((item) => item.isChecked)
            .map((item) => item.key)}
          onChange={(keys) =>
            submenu.items
              .find((item) => item.isChecked !== keys.includes(item.key))
              ?.onClick()
          }
        >
          {submenu.items.map((item) => (
            <FilterMenuOption
              key={item.key}
              value={item.key}
              isDisabled={isDisabled}
              wordBreak="break-word"
              {...{ closeOnSelect: false }}
            >
              {item.label}
            </FilterMenuOption>
          ))}
        </FilterMenuGroup>
      </Box>
      {submenu.footer && (
        <>
          <MenuDivider />
          <MenuItem icon={submenu.footer.icon} onClick={submenu.footer.onClick}>
            {submenu.footer.label}
          </MenuItem>
        </>
      )}
    </>
  );
};

/** Visual-only switch — the menu item (role="menuitemcheckbox") owns the interaction. */
export const ToggleSwitchVisual = ({ isChecked }: { isChecked?: boolean }) => (
  <Box
    aria-hidden
    flexShrink={0}
    width="26px"
    height="16px"
    borderRadius="full"
    bg={isChecked ? "primary.base" : "base.border"}
    position="relative"
    transitionProperty="background-color"
    transitionDuration="fast"
  >
    <Box
      position="absolute"
      top="2px"
      left={isChecked ? "12px" : "2px"}
      width="12px"
      height="12px"
      borderRadius="full"
      bg="white"
      transitionProperty="left"
      transitionDuration="fast"
    />
  </Box>
);
